import type { ToolResult } from "@/types/tools";

export type CurlWarningSeverity = "info" | "warning" | "danger";

export type CurlWarning = {
  id: string;
  severity: CurlWarningSeverity;
  message: string;
};

export type CurlHeader = {
  name: string;
  value: string;
  sensitive: boolean;
};

export type CurlQueryParam = {
  key: string;
  value: string;
  source: "url" | "data";
  sensitive: boolean;
};

export type CurlCookie = {
  name: string;
  value: string;
  sensitive: boolean;
};

export type CurlAuth = {
  type: "basic" | "bearer" | "token" | "unknown";
  username?: string;
  password?: string;
  value?: string;
  sensitive: boolean;
};

export type CurlBodyType = "none" | "json" | "form-urlencoded" | "multipart" | "raw" | "binary";

export type CurlBodyField = {
  key: string;
  value: string;
  kind: "field" | "file" | "raw";
  sensitive: boolean;
};

export type CurlBody = {
  type: CurlBodyType;
  raw: string;
  fields: CurlBodyField[];
  json?: unknown;
  contentType?: string;
};

export type CurlOptions = {
  followRedirects: boolean;
  insecure: boolean;
  compressed: boolean;
  includeHeaders: boolean;
  headOnly: boolean;
  verbose: boolean;
  silent: boolean;
  getWithData: boolean;
  maxTime?: string;
  connectTimeout?: string;
  retry?: string;
  output?: string;
  httpVersion?: string;
};

export type CurlUrlDetails = {
  raw: string;
  href?: string;
  origin?: string;
  protocol?: string;
  hostname?: string;
  port?: string;
  pathname?: string;
  search?: string;
  hash?: string;
  username?: string;
  password?: string;
  valid: boolean;
};

export type CurlTranslation = {
  command: string;
  tokens: string[];
  method: string;
  url: CurlUrlDetails;
  queryParams: CurlQueryParam[];
  headers: CurlHeader[];
  cookies: CurlCookie[];
  auth: CurlAuth | null;
  body: CurlBody;
  options: CurlOptions;
  warnings: CurlWarning[];
  unsupportedFlags: string[];
  explanation: string[];
};

export type CurlSnippetKey = "fetch" | "axios" | "python" | "httpie" | "curl";

export type CurlSnippetSet = Record<CurlSnippetKey, string>;

type DataPart = {
  value: string;
  source: "data" | "data-binary" | "data-urlencode" | "json";
};

type FormPart = {
  value: string;
  forceString: boolean;
};

type ParserState = {
  command: string;
  tokens: string[];
  url: string;
  explicitMethod: string;
  headers: CurlHeader[];
  dataParts: DataPart[];
  formParts: FormPart[];
  cookieInputs: string[];
  authInput: string;
  options: CurlOptions;
  warnings: CurlWarning[];
  unsupportedFlags: string[];
};

const valueFlagNames = new Set([
  "-A",
  "--user-agent",
  "-b",
  "--cookie",
  "--connect-timeout",
  "-d",
  "--data",
  "--data-ascii",
  "--data-binary",
  "--data-raw",
  "--data-urlencode",
  "-e",
  "--referer",
  "-F",
  "--form",
  "--form-string",
  "-H",
  "--header",
  "--max-time",
  "-o",
  "--output",
  "--retry",
  "-u",
  "--user",
  "--url",
  "--url-query",
  "-X",
  "--request",
]);

const booleanShortFlags: Record<string, (state: ParserState) => void> = {
  G: (state) => {
    state.options.getWithData = true;
  },
  I: (state) => {
    state.options.headOnly = true;
  },
  i: (state) => {
    state.options.includeHeaders = true;
  },
  k: (state) => {
    state.options.insecure = true;
  },
  L: (state) => {
    state.options.followRedirects = true;
  },
  s: (state) => {
    state.options.silent = true;
  },
  S: () => undefined,
  v: (state) => {
    state.options.verbose = true;
  },
};

const secretNamePattern =
  /(?:authorization|api[-_]?key|token|secret|password|passwd|pwd|session|cookie|csrf|xsrf|private[-_]?key)/i;

const secretValuePattern =
  /(?:bearer\s+\S+|basic\s+\S+|sk_(?:live|test)_[a-z0-9]+|ghp_[a-z0-9_]+|xox[baprs]-[a-z0-9-]+|eyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+)/i;

function addWarning(
  warnings: CurlWarning[],
  id: string,
  severity: CurlWarningSeverity,
  message: string,
) {
  if (warnings.some((warning) => warning.id === id && warning.message === message)) {
    return;
  }

  warnings.push({ id, severity, message });
}

function createInitialState(command: string, tokens: string[]): ParserState {
  return {
    command,
    tokens,
    url: "",
    explicitMethod: "",
    headers: [],
    dataParts: [],
    formParts: [],
    cookieInputs: [],
    authInput: "",
    options: {
      followRedirects: false,
      insecure: false,
      compressed: false,
      includeHeaders: false,
      headOnly: false,
      verbose: false,
      silent: false,
      getWithData: false,
    },
    warnings: [],
    unsupportedFlags: [],
  };
}

function normalizeCommand(input: string) {
  return input
    .replace(/\r\n/g, "\n")
    .replace(/\\\n/g, " ")
    .replace(/`\n/g, " ")
    .replace(/\^\n/g, " ")
    .trim();
}

export function tokenizeCurlCommand(input: string): ToolResult<string[]> {
  const normalized = normalizeCommand(input);

  if (!normalized) {
    return {
      ok: false,
      error: "Paste a cURL command first.",
    };
  }

  const tokens: string[] = [];
  let current = "";
  let quote: "'" | '"' | null = null;

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    const next = normalized[index + 1];

    if (quote === "'") {
      if (char === "'") {
        quote = null;
      } else {
        current += char;
      }
      continue;
    }

    if (quote === '"') {
      if (char === '"') {
        quote = null;
      } else if (char === "\\" && next) {
        current += next;
        index += 1;
      } else {
        current += char;
      }
      continue;
    }

    if (/\s/.test(char)) {
      if (current) {
        tokens.push(current);
        current = "";
      }
      continue;
    }

    if (char === "'" || char === '"') {
      quote = char;
      continue;
    }

    if (char === "\\" && next) {
      current += next;
      index += 1;
      continue;
    }

    current += char;
  }

  if (quote) {
    return {
      ok: false,
      error: `The command has an unfinished ${quote === "'" ? "single" : "double"} quote.`,
    };
  }

  if (current) {
    tokens.push(current);
  }

  while (tokens[0] === "$" || tokens[0] === ">" || tokens[0] === "PS>") {
    tokens.shift();
  }

  const curlIndex = tokens.findIndex((token) => /^curl(?:\.exe)?$/i.test(token));
  if (curlIndex > 0 && curlIndex <= 2) {
    tokens.splice(0, curlIndex);
  }

  return {
    ok: true,
    value: tokens,
  };
}

function readNextValue(tokens: string[], index: number, flag: string): ToolResult<{ value: string; index: number }> {
  const value = tokens[index + 1];

  if (value === undefined) {
    return {
      ok: false,
      error: `${flag} needs a value.`,
    };
  }

  return {
    ok: true,
    value: {
      value,
      index: index + 1,
    },
  };
}

function readLongValue(token: string, tokens: string[], index: number, flag: string) {
  if (token === flag) {
    return readNextValue(tokens, index, flag);
  }

  if (token.startsWith(`${flag}=`)) {
    return {
      ok: true as const,
      value: {
        value: token.slice(flag.length + 1),
        index,
      },
    };
  }

  return null;
}

function readShortValue(token: string, tokens: string[], index: number, flag: string) {
  if (token === flag) {
    return readNextValue(tokens, index, flag);
  }

  if (token.startsWith(flag) && token.length > flag.length) {
    return {
      ok: true as const,
      value: {
        value: token.slice(flag.length),
        index,
      },
    };
  }

  return null;
}

function parseHeader(input: string, warnings: CurlWarning[]): CurlHeader | null {
  const separatorIndex = input.indexOf(":");

  if (separatorIndex === -1) {
    addWarning(
      warnings,
      `header:${input}`,
      "warning",
      `Header "${input}" does not contain a colon, so it may not be sent as intended.`,
    );
    return null;
  }

  const name = input.slice(0, separatorIndex).trim();
  const value = input.slice(separatorIndex + 1).trim();

  if (!name) {
    addWarning(warnings, "empty-header-name", "warning", "One header has an empty name.");
    return null;
  }

  return {
    name,
    value,
    sensitive: isSensitivePair(name, value),
  };
}

function setHeader(state: ParserState, name: string, value: string) {
  state.headers.push({
    name,
    value,
    sensitive: isSensitivePair(name, value),
  });
}

function getHeaderValue(headers: CurlHeader[], name: string) {
  return headers.find((header) => header.name.toLowerCase() === name.toLowerCase())?.value;
}

function hasHeader(headers: CurlHeader[], name: string) {
  return headers.some((header) => header.name.toLowerCase() === name.toLowerCase());
}

function parseCookieInput(input: string): CurlCookie[] {
  if (!input.includes("=")) {
    return [];
  }

  return input
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const separatorIndex = part.indexOf("=");
      const name = separatorIndex === -1 ? part : part.slice(0, separatorIndex).trim();
      const value = separatorIndex === -1 ? "" : part.slice(separatorIndex + 1).trim();

      return {
        name,
        value,
        sensitive: true,
      };
    });
}

function parseAuth(input: string): CurlAuth | null {
  if (!input) {
    return null;
  }

  const separatorIndex = input.indexOf(":");
  if (separatorIndex !== -1) {
    return {
      type: "basic",
      username: input.slice(0, separatorIndex),
      password: input.slice(separatorIndex + 1),
      sensitive: true,
    };
  }

  return {
    type: "unknown",
    username: input,
    sensitive: true,
  };
}

function detectHeaderAuth(headers: CurlHeader[]): CurlAuth | null {
  const authorization = getHeaderValue(headers, "authorization");

  if (!authorization) {
    return null;
  }

  if (/^bearer\s+/i.test(authorization)) {
    return {
      type: "bearer",
      value: authorization.replace(/^bearer\s+/i, ""),
      sensitive: true,
    };
  }

  if (/^basic\s+/i.test(authorization)) {
    return {
      type: "basic",
      value: authorization,
      sensitive: true,
    };
  }

  return {
    type: "token",
    value: authorization,
    sensitive: true,
  };
}

function parseBodyFields(raw: string): CurlBodyField[] {
  if (!raw || raw.startsWith("@")) {
    return [];
  }

  const params = new URLSearchParams(raw);
  const fields: CurlBodyField[] = [];

  params.forEach((value, key) => {
    fields.push({
      key,
      value,
      kind: "field",
      sensitive: isSensitivePair(key, value),
    });
  });

  return fields;
}

function parseFormPart(part: FormPart): CurlBodyField {
  const separatorIndex = part.value.indexOf("=");
  if (separatorIndex === -1) {
    return {
      key: part.value,
      value: "",
      kind: "raw",
      sensitive: looksSensitive(part.value),
    };
  }

  const key = part.value.slice(0, separatorIndex).trim();
  const value = part.value.slice(separatorIndex + 1);
  const isFile = !part.forceString && (value.startsWith("@") || value.startsWith("<"));

  return {
    key,
    value,
    kind: isFile ? "file" : "field",
    sensitive: isSensitivePair(key, value),
  };
}

function parseJson(raw: string): ToolResult<unknown> {
  try {
    return {
      ok: true,
      value: JSON.parse(raw),
    };
  } catch {
    return {
      ok: false,
      error: "The body looks like JSON, but it is not valid JSON.",
    };
  }
}

function buildBody(state: ParserState): CurlBody {
  if (state.formParts.length) {
    const fields = state.formParts.map(parseFormPart);
    const fileField = fields.find((field) => field.kind === "file");

    if (fileField) {
      addWarning(
        state.warnings,
        "file-upload",
        "info",
        "This command references a local file path. Generated snippets keep a placeholder for the file.",
      );
    }

    return {
      type: "multipart",
      raw: state.formParts.map((part) => part.value).join("\n"),
      fields,
      contentType: "multipart/form-data",
    };
  }

  if (state.options.getWithData) {
    return {
      type: "none",
      raw: "",
      fields: [],
    };
  }

  const bodyParts = state.dataParts.filter((part) => part.source !== "data-urlencode" || !state.options.getWithData);
  if (!bodyParts.length) {
    return {
      type: "none",
      raw: "",
      fields: [],
    };
  }

  const raw = bodyParts.map((part) => part.value).join("&");
  const contentType = getHeaderValue(state.headers, "content-type");
  const cameFromJsonFlag = bodyParts.some((part) => part.source === "json");
  const looksLikeJson =
    cameFromJsonFlag ||
    contentType?.toLowerCase().includes("json") ||
    raw.trim().startsWith("{") ||
    raw.trim().startsWith("[");

  if (raw.startsWith("@") || bodyParts.some((part) => part.source === "data-binary" && part.value.startsWith("@"))) {
    addWarning(
      state.warnings,
      "data-file",
      "info",
      "The request body reads from a local file path. Generated snippets keep a placeholder for the file content.",
    );

    return {
      type: "binary",
      raw,
      fields: [],
      contentType,
    };
  }

  if (looksLikeJson) {
    const parsed = parseJson(raw);
    if (!parsed.ok) {
      addWarning(state.warnings, "invalid-json-body", "warning", parsed.error);
      return {
        type: "raw",
        raw,
        fields: [],
        contentType,
      };
    }

    return {
      type: "json",
      raw,
      fields: [],
      json: parsed.value,
      contentType: contentType ?? "application/json",
    };
  }

  const fields = parseBodyFields(raw);
  const isUrlEncoded =
    contentType?.toLowerCase().includes("application/x-www-form-urlencoded") ||
    (fields.length > 0 && raw.includes("="));

  if (isUrlEncoded) {
    return {
      type: "form-urlencoded",
      raw,
      fields,
      contentType: contentType ?? "application/x-www-form-urlencoded",
    };
  }

  return {
    type: "raw",
    raw,
    fields: [],
    contentType,
  };
}

function parseUrlDetails(rawUrl: string, dataParams: CurlQueryParam[], warnings: CurlWarning[]): CurlUrlDetails {
  if (!rawUrl) {
    return {
      raw: "",
      valid: false,
    };
  }

  try {
    const url = new URL(rawUrl);
    for (const param of dataParams) {
      url.searchParams.append(param.key, param.value);
    }

    if (url.username || url.password) {
      addWarning(
        warnings,
        "url-credentials",
        "danger",
        "The URL contains credentials. Prefer Authorization headers or an auth option.",
      );
    }

    return {
      raw: dataParams.length ? url.href : rawUrl,
      href: url.href,
      origin: url.origin,
      protocol: url.protocol,
      hostname: url.hostname,
      port: url.port,
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      username: url.username,
      password: url.password,
      valid: true,
    };
  } catch {
    if (containsShellVariable(rawUrl)) {
      addWarning(
        warnings,
        "url-shell-variable",
        "info",
        "The URL contains a shell variable, so the exact final URL cannot be resolved locally.",
      );
    } else {
      addWarning(warnings, "invalid-url", "warning", "The URL could not be parsed as a complete URL.");
    }

    return {
      raw: rawUrl,
      valid: false,
    };
  }
}

function getUrlQueryParams(rawUrl: string): CurlQueryParam[] {
  try {
    const url = new URL(rawUrl);
    const params: CurlQueryParam[] = [];
    url.searchParams.forEach((value, key) => {
      params.push({
        key,
        value,
        source: "url",
        sensitive: isSensitivePair(key, value),
      });
    });
    return params;
  } catch {
    return [];
  }
}

function getDataQueryParams(dataParts: DataPart[]): CurlQueryParam[] {
  const params: CurlQueryParam[] = [];

  for (const part of dataParts) {
    if (part.value.startsWith("@")) {
      continue;
    }

    const fields = parseBodyFields(part.value);
    for (const field of fields) {
      params.push({
        key: field.key,
        value: field.value,
        source: "data",
        sensitive: field.sensitive,
      });
    }
  }

  return params;
}

function deriveMethod(state: ParserState, body: CurlBody) {
  if (state.explicitMethod) {
    return state.explicitMethod.toUpperCase();
  }

  if (state.options.headOnly) {
    return "HEAD";
  }

  if (state.options.getWithData) {
    return "GET";
  }

  if (body.type !== "none") {
    return "POST";
  }

  return "GET";
}

function collectWarnings(state: ParserState, translation: Omit<CurlTranslation, "warnings" | "explanation">) {
  const headerCounts = new Map<string, number>();

  for (const header of translation.headers) {
    const key = header.name.toLowerCase();
    headerCounts.set(key, (headerCounts.get(key) ?? 0) + 1);
  }

  for (const [name, count] of headerCounts) {
    if (count > 1) {
      addWarning(
        state.warnings,
        `duplicate-header:${name}`,
        "warning",
        `Header "${name}" appears ${count} times. Later values may override earlier values in generated code.`,
      );
    }
  }

  if (translation.options.insecure) {
    addWarning(
      state.warnings,
      "insecure",
      "danger",
      "The command disables TLS certificate verification with -k/--insecure.",
    );
  }

  if (translation.method === "GET" && translation.body.type !== "none") {
    addWarning(
      state.warnings,
      "get-body",
      "warning",
      "This is a GET request with a body. Many servers and clients ignore or reject GET bodies.",
    );
  }

  if (translation.body.type !== "none" && !hasHeader(translation.headers, "content-type")) {
    addWarning(
      state.warnings,
      "missing-content-type",
      "info",
      "The request has a body but no explicit Content-Type header.",
    );
  }

  if (translation.body.type === "multipart" && hasHeader(translation.headers, "content-type")) {
    addWarning(
      state.warnings,
      "multipart-content-type",
      "warning",
      "For multipart requests, generated browser code should usually let FormData set the Content-Type boundary.",
    );
  }

  for (const token of translation.tokens) {
    if (containsShellVariable(token)) {
      addWarning(
        state.warnings,
        `shell-variable:${token}`,
        "info",
        `Shell variable "${token}" cannot be resolved by the translator.`,
      );
    }
  }
}

function buildExplanation(translation: Omit<CurlTranslation, "warnings" | "explanation">): string[] {
  const lines = [`Sends a ${translation.method} request to ${translation.url.raw || "an unresolved URL"}.`];

  if (translation.queryParams.length) {
    lines.push(
      `Includes ${translation.queryParams.length} query ${translation.queryParams.length === 1 ? "parameter" : "parameters"}.`,
    );
  }

  if (translation.headers.length) {
    const sensitiveHeaders = translation.headers.filter((header) => header.sensitive).length;
    lines.push(
      `Adds ${translation.headers.length} request ${translation.headers.length === 1 ? "header" : "headers"}${
        sensitiveHeaders ? `, with ${sensitiveHeaders} likely sensitive` : ""
      }.`,
    );
  }

  if (translation.auth) {
    const authLabel =
      translation.auth.type === "basic"
        ? "Basic authentication"
        : translation.auth.type === "bearer"
          ? "Bearer token authentication"
          : "an authorization value";
    lines.push(`Uses ${authLabel}.`);
  }

  if (translation.cookies.length) {
    lines.push(`Sends ${translation.cookies.length} cookie ${translation.cookies.length === 1 ? "value" : "values"}.`);
  }

  if (translation.body.type === "json") {
    const keys =
      translation.body.json && typeof translation.body.json === "object" && !Array.isArray(translation.body.json)
        ? Object.keys(translation.body.json).slice(0, 5)
        : [];
    lines.push(
      keys.length
        ? `Sends a JSON body with ${keys.map((key) => `"${key}"`).join(", ")}.`
        : "Sends a JSON body.",
    );
  } else if (translation.body.type === "form-urlencoded") {
    lines.push(`Sends URL-encoded form data with ${translation.body.fields.length} fields.`);
  } else if (translation.body.type === "multipart") {
    const files = translation.body.fields.filter((field) => field.kind === "file").length;
    lines.push(
      `Sends multipart form data with ${translation.body.fields.length} fields${
        files ? `, including ${files} file ${files === 1 ? "upload" : "uploads"}` : ""
      }.`,
    );
  } else if (translation.body.type === "binary") {
    lines.push("Sends a binary or file-backed request body.");
  } else if (translation.body.type === "raw") {
    lines.push("Sends a raw request body.");
  }

  const optionNotes = [];
  if (translation.options.followRedirects) {
    optionNotes.push("follows redirects");
  }
  if (translation.options.compressed) {
    optionNotes.push("asks for compressed responses");
  }
  if (translation.options.insecure) {
    optionNotes.push("skips TLS verification");
  }
  if (translation.options.includeHeaders) {
    optionNotes.push("prints response headers");
  }

  if (optionNotes.length) {
    lines.push(`It also ${joinHumanList(optionNotes)}.`);
  }

  return lines;
}

function parseTokens(state: ParserState): ToolResult<void> {
  const { tokens } = state;

  if (!/^curl(?:\.exe)?$/i.test(tokens[0] ?? "")) {
    return {
      ok: false,
      error: "The command should start with curl.",
    };
  }

  for (let index = 1; index < tokens.length; index += 1) {
    const token = tokens[index];

    const requestValue =
      readShortValue(token, tokens, index, "-X") ?? readLongValue(token, tokens, index, "--request");
    if (requestValue) {
      if (!requestValue.ok) {
        return requestValue;
      }
      state.explicitMethod = requestValue.value.value;
      index = requestValue.value.index;
      continue;
    }

    const headerValue =
      readShortValue(token, tokens, index, "-H") ?? readLongValue(token, tokens, index, "--header");
    if (headerValue) {
      if (!headerValue.ok) {
        return headerValue;
      }
      const header = parseHeader(headerValue.value.value, state.warnings);
      if (header) {
        state.headers.push(header);
      }
      index = headerValue.value.index;
      continue;
    }

    const dataRawValue =
      readLongValue(token, tokens, index, "--data-raw") ??
      readLongValue(token, tokens, index, "--data-ascii") ??
      readLongValue(token, tokens, index, "--data");
    const dataShortValue = dataRawValue ?? readShortValue(token, tokens, index, "-d");
    if (dataShortValue) {
      if (!dataShortValue.ok) {
        return dataShortValue;
      }
      state.dataParts.push({ value: dataShortValue.value.value, source: "data" });
      index = dataShortValue.value.index;
      continue;
    }

    const dataBinaryValue = readLongValue(token, tokens, index, "--data-binary");
    if (dataBinaryValue) {
      if (!dataBinaryValue.ok) {
        return dataBinaryValue;
      }
      state.dataParts.push({ value: dataBinaryValue.value.value, source: "data-binary" });
      index = dataBinaryValue.value.index;
      continue;
    }

    const dataUrlEncodeValue = readLongValue(token, tokens, index, "--data-urlencode");
    if (dataUrlEncodeValue) {
      if (!dataUrlEncodeValue.ok) {
        return dataUrlEncodeValue;
      }
      state.dataParts.push({ value: dataUrlEncodeValue.value.value, source: "data-urlencode" });
      index = dataUrlEncodeValue.value.index;
      continue;
    }

    const jsonValue = readLongValue(token, tokens, index, "--json");
    if (jsonValue) {
      if (!jsonValue.ok) {
        return jsonValue;
      }
      state.dataParts.push({ value: jsonValue.value.value, source: "json" });
      if (!hasHeader(state.headers, "content-type")) {
        setHeader(state, "Content-Type", "application/json");
      }
      if (!hasHeader(state.headers, "accept")) {
        setHeader(state, "Accept", "application/json");
      }
      index = jsonValue.value.index;
      continue;
    }

    const formStringValue = readLongValue(token, tokens, index, "--form-string");
    if (formStringValue) {
      if (!formStringValue.ok) {
        return formStringValue;
      }
      state.formParts.push({ value: formStringValue.value.value, forceString: true });
      index = formStringValue.value.index;
      continue;
    }

    const formValue =
      readShortValue(token, tokens, index, "-F") ?? readLongValue(token, tokens, index, "--form");
    if (formValue) {
      if (!formValue.ok) {
        return formValue;
      }
      state.formParts.push({ value: formValue.value.value, forceString: false });
      index = formValue.value.index;
      continue;
    }

    const userValue =
      readShortValue(token, tokens, index, "-u") ?? readLongValue(token, tokens, index, "--user");
    if (userValue) {
      if (!userValue.ok) {
        return userValue;
      }
      state.authInput = userValue.value.value;
      index = userValue.value.index;
      continue;
    }

    const cookieValue =
      readShortValue(token, tokens, index, "-b") ?? readLongValue(token, tokens, index, "--cookie");
    if (cookieValue) {
      if (!cookieValue.ok) {
        return cookieValue;
      }
      state.cookieInputs.push(cookieValue.value.value);
      index = cookieValue.value.index;
      continue;
    }

    const urlValue = readLongValue(token, tokens, index, "--url");
    if (urlValue) {
      if (!urlValue.ok) {
        return urlValue;
      }
      state.url = urlValue.value.value;
      index = urlValue.value.index;
      continue;
    }

    const userAgentValue =
      readShortValue(token, tokens, index, "-A") ?? readLongValue(token, tokens, index, "--user-agent");
    if (userAgentValue) {
      if (!userAgentValue.ok) {
        return userAgentValue;
      }
      setHeader(state, "User-Agent", userAgentValue.value.value);
      index = userAgentValue.value.index;
      continue;
    }

    const refererValue =
      readShortValue(token, tokens, index, "-e") ?? readLongValue(token, tokens, index, "--referer");
    if (refererValue) {
      if (!refererValue.ok) {
        return refererValue;
      }
      setHeader(state, "Referer", refererValue.value.value);
      index = refererValue.value.index;
      continue;
    }

    const outputValue =
      readShortValue(token, tokens, index, "-o") ?? readLongValue(token, tokens, index, "--output");
    if (outputValue) {
      if (!outputValue.ok) {
        return outputValue;
      }
      state.options.output = outputValue.value.value;
      index = outputValue.value.index;
      continue;
    }

    const maxTimeValue = readLongValue(token, tokens, index, "--max-time");
    if (maxTimeValue) {
      if (!maxTimeValue.ok) {
        return maxTimeValue;
      }
      state.options.maxTime = maxTimeValue.value.value;
      index = maxTimeValue.value.index;
      continue;
    }

    const connectTimeoutValue = readLongValue(token, tokens, index, "--connect-timeout");
    if (connectTimeoutValue) {
      if (!connectTimeoutValue.ok) {
        return connectTimeoutValue;
      }
      state.options.connectTimeout = connectTimeoutValue.value.value;
      index = connectTimeoutValue.value.index;
      continue;
    }

    const retryValue = readLongValue(token, tokens, index, "--retry");
    if (retryValue) {
      if (!retryValue.ok) {
        return retryValue;
      }
      state.options.retry = retryValue.value.value;
      index = retryValue.value.index;
      continue;
    }

    const urlQueryValue = readLongValue(token, tokens, index, "--url-query");
    if (urlQueryValue) {
      if (!urlQueryValue.ok) {
        return urlQueryValue;
      }
      state.dataParts.push({ value: urlQueryValue.value.value, source: "data-urlencode" });
      state.options.getWithData = true;
      index = urlQueryValue.value.index;
      continue;
    }

    if (token === "-G" || token === "--get") {
      state.options.getWithData = true;
      continue;
    }

    if (token === "-I" || token === "--head") {
      state.options.headOnly = true;
      continue;
    }

    if (token === "-i" || token === "--include") {
      state.options.includeHeaders = true;
      continue;
    }

    if (token === "-k" || token === "--insecure") {
      state.options.insecure = true;
      continue;
    }

    if (token === "-L" || token === "--location") {
      state.options.followRedirects = true;
      continue;
    }

    if (token === "-s" || token === "--silent") {
      state.options.silent = true;
      continue;
    }

    if (token === "-v" || token === "--verbose") {
      state.options.verbose = true;
      continue;
    }

    if (token === "--compressed") {
      state.options.compressed = true;
      setHeader(state, "Accept-Encoding", "gzip, deflate, br");
      continue;
    }

    if (token === "--http1.1" || token === "--http2" || token === "--http3") {
      state.options.httpVersion = token.replace(/^--/, "").toUpperCase();
      continue;
    }

    if (/^-[A-Za-z]+$/.test(token) && token.length > 2) {
      for (const shortFlag of token.slice(1)) {
        const handler = booleanShortFlags[shortFlag];
        if (handler) {
          handler(state);
        } else {
          state.unsupportedFlags.push(`-${shortFlag}`);
        }
      }
      continue;
    }

    if (token.startsWith("-")) {
      const flagName = token.includes("=") ? token.slice(0, token.indexOf("=")) : token;
      state.unsupportedFlags.push(flagName);

      if (valueFlagNames.has(flagName) && !token.includes("=")) {
        index += 1;
      }

      continue;
    }

    if (!state.url) {
      state.url = token;
    } else {
      addWarning(
        state.warnings,
        `extra-token:${token}`,
        "info",
        `Extra argument "${token}" was not used while building the request.`,
      );
    }
  }

  return {
    ok: true,
    value: undefined,
  };
}

export function translateCurlCommand(input: string): ToolResult<CurlTranslation> {
  const tokenized = tokenizeCurlCommand(input);
  if (!tokenized.ok) {
    return tokenized;
  }

  const state = createInitialState(input, tokenized.value);
  const parsed = parseTokens(state);
  if (!parsed.ok) {
    return parsed;
  }

  if (!state.url) {
    return {
      ok: false,
      error: "No request URL was found in the cURL command.",
    };
  }

  const cookies = state.cookieInputs.flatMap(parseCookieInput);
  for (const cookieInput of state.cookieInputs) {
    if (!cookieInput.includes("=")) {
      addWarning(
        state.warnings,
        `cookie-file:${cookieInput}`,
        "info",
        `Cookie input "${cookieInput}" looks like a local cookie file path.`,
      );
    }
  }

  const cookieHeader = cookies.map((cookie) => `${cookie.name}=${cookie.value}`).join("; ");
  if (cookieHeader && !hasHeader(state.headers, "cookie")) {
    setHeader(state, "Cookie", cookieHeader);
  }

  const body = buildBody(state);
  const dataQueryParams = state.options.getWithData ? getDataQueryParams(state.dataParts) : [];
  const url = parseUrlDetails(state.url, dataQueryParams, state.warnings);
  const queryParams = [...getUrlQueryParams(state.url), ...dataQueryParams];
  const auth = parseAuth(state.authInput) ?? detectHeaderAuth(state.headers);
  const method = deriveMethod(state, body);

  const baseTranslation = {
    command: input,
    tokens: state.tokens,
    method,
    url,
    queryParams,
    headers: state.headers,
    cookies,
    auth,
    body,
    options: state.options,
    unsupportedFlags: Array.from(new Set(state.unsupportedFlags)),
  };

  collectWarnings(state, baseTranslation);

  if (baseTranslation.unsupportedFlags.length) {
    addWarning(
      state.warnings,
      "unsupported-flags",
      "info",
      `Unsupported flags were preserved as notes: ${baseTranslation.unsupportedFlags.join(", ")}.`,
    );
  }

  return {
    ok: true,
    value: {
      ...baseTranslation,
      warnings: state.warnings,
      explanation: buildExplanation(baseTranslation),
    },
  };
}

export function generateCurlSnippets(
  translation: CurlTranslation,
  options: { redactSecrets?: boolean } = {},
): CurlSnippetSet {
  const redactSecrets = options.redactSecrets ?? true;

  return {
    fetch: generateFetchSnippet(translation, redactSecrets),
    axios: generateAxiosSnippet(translation, redactSecrets),
    python: generatePythonSnippet(translation, redactSecrets),
    httpie: generateHttpieSnippet(translation, redactSecrets),
    curl: generateCanonicalCurl(translation, redactSecrets),
  };
}

export function formatCurlReport(translation: CurlTranslation, redactSecrets = true) {
  const lines = [
    `${translation.method} ${translation.url.raw}`,
    "",
    "Explanation",
    ...translation.explanation.map((line) => `- ${line}`),
  ];

  if (translation.warnings.length) {
    lines.push("", "Warnings", ...translation.warnings.map((warning) => `- ${warning.message}`));
  }

  if (translation.headers.length) {
    lines.push(
      "",
      "Headers",
      ...translation.headers.map(
        (header) => `- ${header.name}: ${displayValue(header.value, header.sensitive, redactSecrets)}`,
      ),
    );
  }

  if (translation.queryParams.length) {
    lines.push(
      "",
      "Query parameters",
      ...translation.queryParams.map(
        (param) => `- ${param.key}: ${displayValue(param.value, param.sensitive, redactSecrets)}`,
      ),
    );
  }

  if (translation.body.type !== "none") {
    lines.push("", "Body", `- Type: ${translation.body.type}`);
    if (translation.body.fields.length) {
      lines.push(
        ...translation.body.fields.map(
          (field) => `- ${field.key}: ${displayValue(field.value, field.sensitive, redactSecrets)}`,
        ),
      );
    } else {
      lines.push(`- ${formatCurlBodyPreview(translation.body, redactSecrets)}`);
    }
  }

  return lines.join("\n");
}

export function formatCurlBodyPreview(body: CurlBody, redactSecrets: boolean) {
  if (!redactSecrets) {
    return body.raw;
  }

  if (body.type === "json" && body.json !== undefined) {
    return JSON.stringify(redactJsonSecrets(body.json, true), null, 2);
  }

  return displayValue(body.raw, looksSensitive(body.raw), redactSecrets);
}

export function displayValue(value: string, sensitive: boolean, redactSecrets: boolean) {
  return redactSecrets && sensitive ? redactSecret(value) : value;
}

export function redactSecret(value: string) {
  if (!value) {
    return "";
  }

  if (/^bearer\s+/i.test(value)) {
    return value.replace(/^bearer\s+.+$/i, "Bearer ***");
  }

  if (/^basic\s+/i.test(value)) {
    return value.replace(/^basic\s+.+$/i, "Basic ***");
  }

  if (value.includes(";") && value.includes("=")) {
    return value
      .split(";")
      .map((part) => {
        const separatorIndex = part.indexOf("=");
        if (separatorIndex === -1) {
          return part.trim();
        }
        return `${part.slice(0, separatorIndex).trim()}=***`;
      })
      .join("; ");
  }

  if (value.includes("=") && value.length < 200) {
    const separatorIndex = value.indexOf("=");
    return `${value.slice(0, separatorIndex + 1)}***`;
  }

  return "***";
}

function generateFetchSnippet(translation: CurlTranslation, redactSecrets: boolean) {
  const headers = getGeneratedHeaders(translation, redactSecrets, "fetch");
  const optionLines: string[] = [];

  if (translation.method !== "GET" || headers.length || translation.body.type !== "none") {
    optionLines.push(`  method: ${quoteJs(translation.method)},`);
  }

  if (headers.length) {
    optionLines.push("  headers: {");
    optionLines.push(...headers.map((header) => `    ${quoteJs(header.name)}: ${quoteJs(header.value)},`));
    optionLines.push("  },");
  }

  const bodyLines = getFetchBodyLines(translation, redactSecrets);
  optionLines.push(...bodyLines);

  const prefix = getFetchPrefix(translation, redactSecrets);
  if (!optionLines.length && !prefix) {
    return `const response = await fetch(${quoteJs(translation.url.raw)});`;
  }

  return [
    prefix,
    `const response = await fetch(${quoteJs(translation.url.raw)}, {`,
    ...optionLines,
    "});",
  ]
    .filter(Boolean)
    .join("\n");
}

function getFetchPrefix(translation: CurlTranslation, redactSecrets: boolean) {
  if (translation.body.type !== "multipart") {
    return "";
  }

  const lines = ["const formData = new FormData();"];
  for (const field of translation.body.fields) {
    if (field.kind === "file") {
      lines.push(`formData.append(${quoteJs(field.key)}, fileInput.files[0]);`);
    } else {
      lines.push(
        `formData.append(${quoteJs(field.key)}, ${quoteJs(displayValue(field.value, field.sensitive, redactSecrets))});`,
      );
    }
  }
  return lines.join("\n");
}

function getFetchBodyLines(translation: CurlTranslation, redactSecrets: boolean) {
  if (translation.body.type === "none") {
    return [];
  }

  if (translation.body.type === "json" && translation.body.json !== undefined) {
    return [`  body: JSON.stringify(${formatJsValue(redactJsonSecrets(translation.body.json, redactSecrets))}),`];
  }

  if (translation.body.type === "form-urlencoded" && translation.body.fields.length) {
    return [`  body: new URLSearchParams(${formatJsObjectFromFields(translation.body.fields, redactSecrets)}),`];
  }

  if (translation.body.type === "multipart") {
    return ["  body: formData,"];
  }

  if (translation.body.type === "binary") {
    return ["  body: fileOrBlob,"];
  }

  return [`  body: ${quoteJs(displayValue(translation.body.raw, looksSensitive(translation.body.raw), redactSecrets))},`];
}

function generateAxiosSnippet(translation: CurlTranslation, redactSecrets: boolean) {
  const headers = getGeneratedHeaders(translation, redactSecrets, "axios");
  const lines = [
    'import axios from "axios";',
    "",
    "const response = await axios({",
    `  method: ${quoteJs(translation.method.toLowerCase())},`,
    `  url: ${quoteJs(translation.url.raw)},`,
  ];

  if (headers.length) {
    lines.push("  headers: {");
    lines.push(...headers.map((header) => `    ${quoteJs(header.name)}: ${quoteJs(header.value)},`));
    lines.push("  },");
  }

  const data = getAxiosDataLine(translation, redactSecrets);
  if (data) {
    lines.push(data);
  }

  if (translation.options.maxTime) {
    const milliseconds = Number(translation.options.maxTime) * 1000;
    if (Number.isFinite(milliseconds)) {
      lines.push(`  timeout: ${milliseconds},`);
    }
  }

  lines.push("});");

  return lines.join("\n");
}

function getAxiosDataLine(translation: CurlTranslation, redactSecrets: boolean) {
  if (translation.body.type === "none") {
    return "";
  }

  if (translation.body.type === "json" && translation.body.json !== undefined) {
    return `  data: ${formatJsValue(redactJsonSecrets(translation.body.json, redactSecrets))},`;
  }

  if (translation.body.type === "form-urlencoded" && translation.body.fields.length) {
    return `  data: new URLSearchParams(${formatJsObjectFromFields(translation.body.fields, redactSecrets)}),`;
  }

  if (translation.body.type === "multipart") {
    return "  data: formData,";
  }

  if (translation.body.type === "binary") {
    return "  data: fileOrBuffer,";
  }

  return `  data: ${quoteJs(displayValue(translation.body.raw, looksSensitive(translation.body.raw), redactSecrets))},`;
}

function generatePythonSnippet(translation: CurlTranslation, redactSecrets: boolean) {
  const headers = getGeneratedHeaders(translation, redactSecrets, "python");
  const lines = ['import requests', "", "response = requests.request("];
  lines.push(`    ${quotePython(translation.method)},`);
  lines.push(`    ${quotePython(translation.url.raw)},`);

  if (headers.length) {
    lines.push("    headers={");
    lines.push(...headers.map((header) => `        ${quotePython(header.name)}: ${quotePython(header.value)},`));
    lines.push("    },");
  }

  if (translation.auth?.type === "basic" && translation.auth.username && !hasHeader(translation.headers, "authorization")) {
    lines.push(
      `    auth=(${quotePython(displayValue(translation.auth.username, false, redactSecrets))}, ${quotePython(
        displayValue(translation.auth.password ?? "", true, redactSecrets),
      )}),`,
    );
  }

  const bodyLine = getPythonBodyLine(translation, redactSecrets);
  if (bodyLine) {
    lines.push(bodyLine);
  }

  if (translation.options.maxTime) {
    lines.push(`    timeout=${quotePython(translation.options.maxTime)},`);
  }

  lines.push(")");
  lines.push("print(response.status_code)");
  lines.push("print(response.text)");

  return lines.join("\n");
}

function getPythonBodyLine(translation: CurlTranslation, redactSecrets: boolean) {
  if (translation.body.type === "none") {
    return "";
  }

  if (translation.body.type === "json" && translation.body.json !== undefined) {
    return `    json=${indentPythonContinuation(
      formatPythonValue(redactJsonSecrets(translation.body.json, redactSecrets)),
    )},`;
  }

  if (translation.body.type === "form-urlencoded" && translation.body.fields.length) {
    return `    data=${formatPythonObjectFromFields(translation.body.fields, redactSecrets)},`;
  }

  if (translation.body.type === "multipart") {
    const fieldLines = translation.body.fields.map((field) => {
      if (field.kind === "file") {
        return `        ${quotePython(field.key)}: open(${quotePython(field.value.replace(/^[@<]/, ""))}, "rb"),`;
      }
      return `        ${quotePython(field.key)}: ${quotePython(displayValue(field.value, field.sensitive, redactSecrets))},`;
    });

    return ["    files={", ...fieldLines, "    },"].join("\n");
  }

  if (translation.body.type === "binary") {
    return "    data=file_or_bytes,";
  }

  return `    data=${quotePython(displayValue(translation.body.raw, looksSensitive(translation.body.raw), redactSecrets))},`;
}

function generateHttpieSnippet(translation: CurlTranslation, redactSecrets: boolean) {
  const parts = ["http"];

  if (translation.options.followRedirects) {
    parts.push("--follow");
  }

  if (translation.options.insecure) {
    parts.push("--verify=no");
  }

  parts.push(translation.method);
  parts.push(shellQuote(translation.url.raw));

  for (const header of getGeneratedHeaders(translation, redactSecrets, "httpie")) {
    parts.push(`${header.name}:${shellQuote(header.value)}`);
  }

  if (translation.body.type === "json" && translation.body.json && typeof translation.body.json === "object") {
    for (const [key, value] of Object.entries(flattenPrimitiveObject(translation.body.json))) {
      parts.push(`${key}:=${shellQuote(JSON.stringify(value))}`);
    }
  } else if (translation.body.fields.length) {
    for (const field of translation.body.fields) {
      if (field.kind === "file") {
        parts.push(`${field.key}@${shellQuote(field.value.replace(/^[@<]/, ""))}`);
      } else {
        parts.push(`${field.key}=${shellQuote(displayValue(field.value, field.sensitive, redactSecrets))}`);
      }
    }
  } else if (translation.body.raw) {
    parts.push(`<<< ${shellQuote(displayValue(translation.body.raw, looksSensitive(translation.body.raw), redactSecrets))}`);
  }

  return parts.join(" ");
}

function generateCanonicalCurl(translation: CurlTranslation, redactSecrets: boolean) {
  const lines = [`curl ${shellQuote(translation.url.raw)}`];

  if (translation.method !== "GET") {
    lines.push(`  -X ${translation.method}`);
  }

  for (const header of translation.headers) {
    lines.push(`  -H ${shellQuote(`${header.name}: ${displayValue(header.value, header.sensitive, redactSecrets)}`)}`);
  }

  if (translation.options.followRedirects) {
    lines.push("  --location");
  }

  if (translation.options.insecure) {
    lines.push("  --insecure");
  }

  if (translation.body.type === "json") {
    lines.push(`  --data-raw ${shellQuote(displayValue(translation.body.raw, looksSensitive(translation.body.raw), redactSecrets))}`);
  } else if (translation.body.type === "form-urlencoded" || translation.body.type === "raw") {
    lines.push(`  --data-raw ${shellQuote(displayValue(translation.body.raw, looksSensitive(translation.body.raw), redactSecrets))}`);
  } else if (translation.body.type === "multipart") {
    for (const field of translation.body.fields) {
      lines.push(`  -F ${shellQuote(`${field.key}=${displayValue(field.value, field.sensitive, redactSecrets)}`)}`);
    }
  } else if (translation.body.type === "binary") {
    lines.push(`  --data-binary ${shellQuote(translation.body.raw)}`);
  }

  return lines.join(" \\\n");
}

function getGeneratedHeaders(
  translation: CurlTranslation,
  redactSecrets: boolean,
  target: "fetch" | "axios" | "python" | "httpie",
) {
  const headers = translation.headers
    .filter((header) => {
      if (translation.body.type === "multipart" && header.name.toLowerCase() === "content-type" && target === "fetch") {
        return false;
      }
      return true;
    })
    .map((header) => ({
      name: header.name,
      value: displayValue(header.value, header.sensitive, redactSecrets),
    }));

  if (translation.auth?.type === "basic" && translation.auth.username && !hasHeader(translation.headers, "authorization")) {
    headers.push({
      name: "Authorization",
      value: redactSecrets
        ? "Basic ***"
        : `Basic ${base64Encode(`${translation.auth.username}:${translation.auth.password ?? ""}`)}`,
    });
  }

  return headers;
}

function formatJsObjectFromFields(fields: CurlBodyField[], redactSecrets: boolean) {
  const entries = fields.map(
    (field) =>
      `${JSON.stringify(field.key)}: ${JSON.stringify(displayValue(field.value, field.sensitive, redactSecrets))}`,
  );
  return `{ ${entries.join(", ")} }`;
}

function formatPythonObjectFromFields(fields: CurlBodyField[], redactSecrets: boolean) {
  const entries = fields.map(
    (field) =>
      `${quotePython(field.key)}: ${quotePython(displayValue(field.value, field.sensitive, redactSecrets))}`,
  );
  return `{${entries.join(", ")}}`;
}

function redactJsonSecrets(value: unknown, redactSecrets: boolean): unknown {
  if (!redactSecrets) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => redactJsonSecrets(item, redactSecrets));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        key,
        isSensitivePair(key, String(nested)) ? redactSecret(String(nested)) : redactJsonSecrets(nested, redactSecrets),
      ]),
    );
  }

  return value;
}

function formatJsValue(value: unknown) {
  return JSON.stringify(value, null, 2)
    .split("\n")
    .map((line, index) => (index === 0 ? line : `  ${line}`))
    .join("\n");
}

function formatPythonValue(value: unknown): string {
  if (value === null) {
    return "None";
  }

  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "None";
  }

  if (typeof value === "string") {
    return quotePython(value);
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return "[]";
    }

    return `[\n${value.map((item) => indentPython(formatPythonValue(item))).join(",\n")}\n]`;
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) {
      return "{}";
    }

    return `{\n${entries
      .map(([key, nested]) => indentPython(`${quotePython(key)}: ${formatPythonValue(nested)}`))
      .join(",\n")}\n}`;
  }

  return "None";
}

function quoteJs(value: string) {
  return JSON.stringify(value);
}

function quotePython(value: string) {
  return JSON.stringify(value);
}

function indentPython(value: string) {
  return value
    .split("\n")
    .map((line) => `    ${line}`)
    .join("\n");
}

function indentPythonContinuation(value: string) {
  return value.replace(/\n/g, "\n    ");
}

function shellQuote(value: string) {
  if (/^[a-zA-Z0-9_./:=@%+-]+$/.test(value)) {
    return value;
  }

  return `'${value.replace(/'/g, "'\\''")}'`;
}

function flattenPrimitiveObject(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      ([, nested]) =>
        nested === null ||
        typeof nested === "string" ||
        typeof nested === "number" ||
        typeof nested === "boolean",
    ),
  );
}

function isSensitivePair(name: string, value: string) {
  return secretNamePattern.test(name) || looksSensitive(value);
}

function looksSensitive(value: string) {
  return secretValuePattern.test(value);
}

function containsShellVariable(value: string) {
  return /\$\{?[A-Za-z_][A-Za-z0-9_]*\}?|%[A-Za-z_][A-Za-z0-9_]*%|\$env:[A-Za-z_][A-Za-z0-9_]*/.test(value);
}

function joinHumanList(values: string[]) {
  if (values.length <= 1) {
    return values[0] ?? "";
  }

  if (values.length === 2) {
    return `${values[0]} and ${values[1]}`;
  }

  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}

function base64Encode(value: string) {
  if (typeof btoa === "function") {
    return btoa(value);
  }

  return value;
}
